"use client";

/**
 * FontPicker — one font chooser used everywhere fonts are selected: the Super
 * Admin app-wide typeface, a user's profile/wall font, and the creative tools.
 *
 * Offers the FULL Google Fonts library (~1900 families, lazily fetched from
 * public/font-catalog.json) plus every uploaded cloud font, plus the system
 * stack. Uploading here puts the file in Firebase Storage + the shared
 * `customFonts` index, so a font imported once — a licensed face like Bell
 * Gothic, say — becomes available to every picker in the app.
 *
 * Previews render each option in its own face. Only the visible page of
 * results is loaded, batched into one stylesheet request, so opening the
 * picker never fires hundreds of requests.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, Upload, Loader2, Check, Type, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  fetchFontCatalog, fetchCloudFonts, loadAllCloudFonts,   deleteCloudFont, ensureGoogleFontsLoaded, ensureFontLoaded, importFontFile,
  SYSTEM_FONT_NAMES, FONT_CATEGORY_LABELS,
  type CatalogFont, type CloudFont, type FontCategory,
} from "@/lib/fonts";

/** How many results to render at once. The list is popularity-sorted, so the
 *  first page is the fonts people actually reach for; search narrows the rest. */
const PAGE = 60;

type Group = "all" | "uploaded" | "system" | FontCategory;

const GROUPS: { key: Group; label: string }[] = [
  { key: "all", label: "All" },
  { key: "uploaded", label: "Uploaded" },
  { key: "system", label: "System" },
  { key: "sans", label: "Sans" },
  { key: "display", label: "Display" },
  { key: "serif", label: "Serif" },
  { key: "script", label: "Script" },
  { key: "mono", label: "Mono" },
];

export interface FontPickerProps {
  value?: string;
  onChange: (family: string) => void;
  /** Show the upload control. Turn off for contexts where users shouldn't
   *  add to the shared library. */
  allowUpload?: boolean;
  /** Let the user clear the selection back to the default typeface. */
  allowClear?: boolean;
  /** Who is uploading — recorded on the cloud font for attribution. */
  uploadedBy?: string;
  /** Allow removing an uploaded font from the shared index (admins only). */
  allowDelete?: boolean;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Drive the dialog from outside (with hideTrigger) to open the browser from
   *  an existing control instead of this component's own button. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}

export function FontPicker({
  value,
  onChange,
  allowUpload = true,
  allowClear = false,
  uploadedBy,
  allowDelete = false,
  className,
  placeholder = "Default font",
  disabled,
  open: openProp,
  onOpenChange,
  hideTrigger,
}: FontPickerProps) {
  const { toast } = useToast();
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = useCallback((o: boolean) => {
    setOpenState(o);
    onOpenChange?.(o);
  }, [onOpenChange]);
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState<Group>("all");
  const [catalog, setCatalog] = useState<CatalogFont[]>([]);
  const [cloud, setCloud] = useState<CloudFont[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [limit, setLimit] = useState(PAGE);
  // Licence text pulled out of an imported archive — shown after import so the
  // terms are read BEFORE the font gets used commercially.
  const [licence, setLicence] = useState<{ family: string; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Make sure the CURRENT font renders in the trigger even before opening.
  useEffect(() => { if (value) ensureFontLoaded(value).catch(() => {}); }, [value]);

  // Catalogue + cloud index load on first open, not on mount — a picker that
  // is never opened should cost nothing.
  useEffect(() => {
    if (!open || catalog.length || loading) return;
    setLoading(true);
    Promise.all([fetchFontCatalog(), fetchCloudFonts(), loadAllCloudFonts()])
      .then(([cat, cf]) => { setCatalog(cat); setCloud(cf); })
      .catch(() => toast({ description: "Couldn't load the font library.", variant: "destructive" }))
      .finally(() => setLoading(false));
  }, [open, catalog.length, loading, toast]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const cloudNames = cloud.map(c => c.family);

    let pool: { family: string; kind: "cloud" | "system" | "google"; category?: FontCategory }[] = [];
    if (group === "uploaded") {
      pool = cloudNames.map(f => ({ family: f, kind: "cloud" as const }));
    } else if (group === "system") {
      pool = SYSTEM_FONT_NAMES.map(f => ({ family: f, kind: "system" as const }));
    } else {
      // Uploaded and system fonts sort first — they're the short, curated
      // lists, and burying a just-uploaded font under 1900 Google families
      // would make it feel like the upload failed.
      pool = [
        ...cloudNames.map(f => ({ family: f, kind: "cloud" as const })),
        ...SYSTEM_FONT_NAMES.map(f => ({ family: f, kind: "system" as const })),
        ...catalog
          .filter(c => group === "all" || c.category === group)
          .map(c => ({ family: c.family, kind: "google" as const, category: c.category })),
      ];
    }

    if (q) pool = pool.filter(p => p.family.toLowerCase().includes(q));
    // De-dupe: an uploaded font may share a name with a Google family.
    const seen = new Set<string>();
    return pool.filter(p => (seen.has(p.family) ? false : (seen.add(p.family), true)));
  }, [query, group, catalog, cloud]);

  const visible = results.slice(0, limit);

  // Load only what's on screen, in one batched request per page.
  useEffect(() => {
    if (!open) return;
    ensureGoogleFontsLoaded(visible.filter(v => v.kind === "google").map(v => v.family));
  }, [open, visible]);

  // A new search should start back at the first page.
  useEffect(() => { setLimit(PAGE); }, [query, group]);

  const handleUpload = useCallback(async (file: File) => {
    setUploading(true);
    try {
      // Accepts a bare font file OR a .zip — which is what dafont.com and most
      // free-font sites hand you.
      const { fonts, licence: lic, failed } = await importFontFile(file, uploadedBy);
      setCloud(prev => [...prev, ...fonts]);
      onChange(fonts[0].family);
      toast({
        description: fonts.length === 1
          ? `"${fonts[0].family}" imported — now available across the app.`
          : `${fonts.length} fonts imported from ${file.name}.`,
      });
      if (failed.length) {
        toast({ description: `Couldn't import: ${failed.join(", ")}`, variant: "destructive" });
      }
      // A licence file means the archive came with terms — show them rather
      // than closing, since many free fonts are personal-use only.
      if (lic) setLicence({ family: fonts[0].family, text: lic });
      else setOpen(false);
    } catch (err: any) {
      toast({ description: err?.message || "Font import failed.", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  }, [onChange, toast, uploadedBy]);

  const handleDelete = useCallback(async (f: CloudFont) => {
    try {
      await deleteCloudFont(f.id);
      setCloud(prev => prev.filter(c => c.id !== f.id));
      toast({ description: `"${f.family}" removed from the shared library.` });
    } catch {
      toast({ description: "Couldn't remove that font.", variant: "destructive" });
    }
  }, [toast]);

  return (
    <>
      {!hideTrigger && (
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className={cn("w-full justify-between font-normal gap-2", className)}
      >
        <span className="flex items-center gap-2 min-w-0">
          <Type className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span
            className="truncate"
            style={value ? { fontFamily: `"${value}"` } : undefined}
          >
            {value || placeholder}
          </span>
        </span>
        <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
          <DialogHeader className="p-4 pb-3 space-y-3">
            <DialogTitle className="text-base">Choose a font</DialogTitle>

            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input type="search"
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={`Search ${catalog.length ? catalog.length.toLocaleString() : ""} fonts…`}
                className="pl-8 h-9"
              />
            </div>

            <div className="flex flex-wrap gap-1">
              {GROUPS.map(g => (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => setGroup(g.key)}
                  className={cn(
                    "px-2 py-0.5 rounded text-[11px] border transition-colors",
                    group === g.key
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:bg-muted",
                  )}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </DialogHeader>

          <div className="max-h-[52vh] overflow-y-auto border-t border-border">
            {loading && (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading font library…
              </div>
            )}

            {!loading && allowClear && !query && (
              <button
                type="button"
                onClick={() => { onChange(""); setOpen(false); }}
                className="w-full text-left px-4 py-2.5 hover:bg-muted text-sm border-b border-border/60 flex items-center justify-between"
              >
                <span className="text-muted-foreground italic">Default font</span>
                {!value && <Check className="h-4 w-4 text-primary" />}
              </button>
            )}

            {!loading && visible.map(item => {
              const cloudEntry = item.kind === "cloud" ? cloud.find(c => c.family === item.family) : undefined;
              return (
                <div
                  key={item.family}
                  className={cn(
                    "group flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-muted cursor-pointer border-b border-border/40",
                    value === item.family && "bg-primary/10",
                  )}
                  onClick={() => { onChange(item.family); setOpen(false); }}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-base truncate" style={{ fontFamily: `"${item.family}"` }}>
                      {item.family}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {item.kind === "cloud" ? "Uploaded"
                        : item.kind === "system" ? "System"
                        : FONT_CATEGORY_LABELS[item.category as FontCategory] ?? "Google Fonts"}
                    </div>
                  </div>
                  {value === item.family && <Check className="h-4 w-4 text-primary shrink-0" />}
                  {allowDelete && cloudEntry && (
                    <button
                      type="button"
                      title={`Remove "${item.family}" from the shared library`}
                      onClick={e => { e.stopPropagation(); handleDelete(cloudEntry); }}
                      className="bb-hover-reveal p-1 rounded hover:bg-destructive/15 text-destructive shrink-0"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              );
            })}

            {!loading && !visible.length && (
              <p className="py-10 text-center text-sm text-muted-foreground">
                No font matches “{query}”.
              </p>
            )}

            {!loading && results.length > limit && (
              <button
                type="button"
                onClick={() => setLimit(l => l + PAGE)}
                className="w-full py-3 text-xs text-primary hover:bg-muted"
              >
                Show more — {(results.length - limit).toLocaleString()} more
              </button>
            )}
          </div>

          {licence && (
            <div className="p-3 border-t border-border bg-amber-500/10 max-h-56 overflow-y-auto">
              <p className="text-xs font-semibold mb-1">
                Licence included with “{licence.family}”
              </p>
              <p className="text-[10px] text-muted-foreground mb-2">
                Fonts here are shared across the whole platform, which counts as
                commercial use. Many free fonts are personal-use only — read this
                before publishing with it.
              </p>
              <pre className="text-[10px] whitespace-pre-wrap font-mono leading-relaxed opacity-90">
                {licence.text}
              </pre>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-2 h-7 text-xs"
                onClick={() => { setLicence(null); setOpen(false); }}
              >
                Got it
              </Button>
            </div>
          )}

          {allowUpload && (
            <div className="p-3 border-t border-border bg-muted/30">
              <input
                ref={fileRef}
                type="file"
                accept=".ttf,.otf,.woff,.woff2,.zip,font/*,application/zip"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ""; }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full gap-2"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
              >
                {uploading
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…</>
                  : <><Upload className="h-3.5 w-3.5" /> Import a font or .zip (dafont, Font Squirrel…)</>}
              </Button>
              <p className="mt-1.5 text-[10px] text-muted-foreground text-center">
                Drop in a dafont .zip and the font files are extracted
                automatically. Imported fonts become available across the whole
                app — check the licence allows commercial use.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
